/**
 * "فرق التصوير" — the admin manages photography crews.
 *
 * Each crew carries its own hourly rate, working window, accepted job lengths,
 * and the two gaps that shape the whole calendar: travel time between different
 * addresses, and the shorter reset between two shoots at the same one. Both are
 * per team, because a crew working one city needs far less than one covering a
 * governorate — and split by location because charging travel time to a crew
 * that never leaves the building blocks bookings that are genuinely possible.
 *
 * The card and the editor both state the consequence in hours, because these are
 * the numbers most likely to be set without thinking about what they cost: at 60
 * minutes either side a two-hour shoot consumes four hours of the day.
 *
 * Both gaps are only ever *read* here. They are copied onto each booking when it
 * is taken, so widening a crew's gap never retroactively overlaps shoots that
 * were already sold.
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  PageHeader,
  Panel,
  Spinner,
  Textarea,
  TimeInput,
} from '../../components/common';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { photographyApi } from '../../api/endpoints';
import { formatCurrency } from '../../domain/pricing';
import { compressImage, readFileAsDataUri, dataUriByteLength, formatBytes } from '../../domain/files';
import { ARABIC_DAYS, isoToDisplayDate, formatTime12 } from '../../domain/dates';
import { occupiedHours, shootsPerDay } from '../../domain/photography';
import { timeRange } from '../../domain/bidi';

const DAYS = [
  'saturday',
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
];

/** Common travel gaps, so the usual answer is one click. */
const BUFFER_PRESETS = [0, 30, 45, 60, 90, 120];

/** A crew already on site is packing up, not travelling. */
const SAME_LOCATION_PRESETS = [0, 10, 15, 30, 45];

const MAX_PORTFOLIO = 20;
const MAX_FILE_BYTES = 6 * 1024 * 1024;

const emptyTeam = () => ({
  name: '',
  email: '',
  phone: '',
  hourlyRate: 500,
  bufferMinutes: 60,
  sameLocationBufferMinutes: 15,
  workingDays: ['saturday', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
  startTime: '10:00',
  endTime: '18:00',
  minHours: 1,
  maxHours: 6,
  bio: '',
  active: true,
});

export function AdminPhotographyTeams() {
  const { success, error: toastError } = useNotifications();
  const state = useAsync(() => photographyApi.teams(), [], { initialData: [] });
  const bookingsState = useAsync(() => photographyApi.bookings(), [], { initialData: [] });

  const teams = useMemo(() => state.data || [], [state.data]);
  const bookings = useMemo(() => bookingsState.data || [], [bookingsState.data]);

  const [editing, setEditing] = useState(null);
  const [portfolioTeam, setPortfolioTeam] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const liveCount = (teamId) =>
    bookings.filter(
      (b) => String(b.teamId) === String(teamId) && ['pending', 'confirmed'].includes(b.status),
    ).length;

  const save = async (team) => {
    setBusy(true);
    try {
      const payload = {
        ...team,
        hourlyRate: Number(team.hourlyRate) || 0,
        bufferMinutes: Number(team.bufferMinutes) || 0,
        sameLocationBufferMinutes: Number(team.sameLocationBufferMinutes) || 0,
        minHours: Number(team.minHours) || 1,
        maxHours: Number(team.maxHours) || 6,
      };
      if (team.id) await photographyApi.updateTeam(team.id, payload);
      else await photographyApi.createTeam(payload);

      success(team.id ? 'تم تحديث الفريق' : 'تمت إضافة الفريق');
      setEditing(null);
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر حفظ الفريق');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const result = await photographyApi.deleteTeam(deleteTarget.id);
      // A crew with shoots on the books is deactivated rather than deleted —
      // removing it would orphan work a doctor already paid for.
      success(result?.deactivated ? result.message : 'تم حذف الفريق');
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر حذف الفريق');
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="فرق التصوير"
        subtitle={`${teams.length} فريق`}
        icon="camera"
        actions={
          <>
            <Button variant="ghost" icon="refresh-cw" onClick={state.reload} loading={state.loading}>
              تحديث
            </Button>
            <Button icon="plus" onClick={() => setEditing(emptyTeam())}>
              إضافة فريق
            </Button>
          </>
        }
      />

      {state.error ? (
        <ErrorState error={state.error} onRetry={state.reload} />
      ) : state.loading && teams.length === 0 ? (
        <Spinner label="جاري تحميل الفرق..." />
      ) : teams.length === 0 ? (
        <Panel>
          <EmptyState
            icon="camera"
            title="لا توجد فرق تصوير"
            message="أضف فريقاً ليتمكن الأطباء من حجز جلسات تصوير ترويجية."
            action={
              <Button icon="plus" onClick={() => setEditing(emptyTeam())}>
                إضافة فريق
              </Button>
            }
          />
        </Panel>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {teams.map((team) => (
            <TeamCard
              key={team.id}
              team={team}
              liveBookings={liveCount(team.id)}
              onEdit={() => setEditing({ ...emptyTeam(), ...team })}
              onPortfolio={() => setPortfolioTeam(team)}
              onDelete={() => setDeleteTarget(team)}
            />
          ))}
        </div>
      )}

      {/* Every shoot on the books, so the admin can see the crews' load */}
      <Panel>
        <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
          <Icon name="calendar-check" size={18} className="text-purple-400" />
          حجوزات التصوير ({bookings.length})
        </h3>

        {bookingsState.loading && bookings.length === 0 ? (
          <Spinner variant="inline" />
        ) : bookings.length === 0 ? (
          <EmptyState icon="calendar-x" title="لا توجد حجوزات تصوير" />
        ) : (
          <div className="space-y-2">
            {bookings.slice(0, 25).map((booking) => (
              <div
                key={booking.id}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-white/5 bg-white/5 p-3 text-sm"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-bold text-white">
                    {booking.PhotographyTeam?.name || '—'}
                  </span>
                  <span className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-slate-400">
                    <span>د. {booking.Doctor?.name || '—'}</span>
                    <span>{isoToDisplayDate(booking.date) || booking.date}</span>
                    <span dir="ltr">{formatTime12(booking.startTime)}</span>
                    <span>{booking.durationHours} ساعة</span>
                    <span className="text-emerald-400">{formatCurrency(booking.totalPrice)}</span>
                  </span>
                </span>

                <div className="flex gap-1.5">
                  {booking.status !== 'confirmed' && booking.status !== 'cancelled' && (
                    <Button
                      variant="success"
                      icon="check"
                      onClick={async () => {
                        await photographyApi.setStatus(booking.id, 'confirmed');
                        bookingsState.reload();
                      }}
                    >
                      تأكيد
                    </Button>
                  )}
                  {booking.status !== 'cancelled' && (
                    <Button
                      variant="ghost"
                      icon="x"
                      onClick={async () => {
                        await photographyApi.setStatus(booking.id, 'cancelled');
                        bookingsState.reload();
                      }}
                    >
                      إلغاء
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {editing && (
        <TeamEditor team={editing} busy={busy} onClose={() => setEditing(null)} onSave={save} />
      )}

      {portfolioTeam && (
        <PortfolioEditor
          team={portfolioTeam}
          onClose={() => setPortfolioTeam(null)}
          onSaved={() => {
            setPortfolioTeam(null);
            state.reload();
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={remove}
        loading={busy}
        destructive
        title="حذف الفريق"
        confirmLabel="حذف"
        message={
          deleteTarget && liveCount(deleteTarget.id) > 0
            ? `على "${deleteTarget.name}" ${liveCount(deleteTarget.id)} حجز قائم، لذا سيتم إيقافه بدلاً من حذفه — الحجوزات القائمة لن تُلغى.`
            : `سيتم حذف "${deleteTarget?.name || ''}" نهائياً.`
        }
      />
    </div>
  );
}

/* ========================================================================== */

function TeamCard({ team, liveBookings, onEdit, onPortfolio, onDelete }) {
  const days = (team.workingDays || []).map((d) => ARABIC_DAYS[d] || d).join('، ');

  return (
    <Panel className="space-y-3">
      <div className="flex items-start gap-3">
        <span className="rounded-xl bg-purple-500/10 p-3 text-purple-400">
          <Icon name="camera" size={22} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-bold text-white">{team.name}</h3>
            {team.active ? (
              <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300">نشط</Badge>
            ) : (
              <Badge className="border-red-500/30 bg-red-500/10 text-red-300">موقوف</Badge>
            )}
            {liveBookings > 0 && (
              <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300">
                {liveBookings} حجز قائم
              </Badge>
            )}
          </div>

          <p className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-slate-400">
            {team.email && <span dir="ltr">{team.email}</span>}
            {team.phone && <span dir="ltr">{team.phone}</span>}
          </p>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-2 rounded-xl border border-white/5 bg-white/5 p-3 text-xs">
        <Stat label="سعر الساعة" value={formatCurrency(team.hourlyRate)} tone="text-emerald-400" />
        <Stat
          label="انتقال لمكان آخر"
          value={`${team.bufferMinutes} دقيقة`}
          tone="text-amber-400"
        />
        <Stat
          label="نفس المكان"
          value={`${team.sameLocationBufferMinutes} دقيقة`}
          tone="text-emerald-400"
        />
        <Stat label="الدوام" value={timeRange(formatTime12(team.startTime), formatTime12(team.endTime))} />
        <Stat label="مدة الجلسة" value={`${timeRange(team.minHours, team.maxHours)} ساعات`} />
      </dl>

      <p className="text-[11px] text-slate-400">{days || 'لم تُحدد أيام عمل'}</p>

      {/* The consequence of the buffer, stated plainly — it is the number most
          likely to be set without thinking about what it costs. */}
      <p className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-2 text-[11px] leading-relaxed text-amber-200">
        جلسة {team.minHours} ساعة تشغل{' '}
        <strong>{occupiedHours({ hours: team.minHours, bufferMinutes: team.bufferMinutes })}</strong>{' '}
        ساعة في مكان جديد، أو{' '}
        <strong>
          {occupiedHours({
            hours: team.minHours,
            bufferMinutes: team.sameLocationBufferMinutes,
          })}
        </strong>{' '}
        ساعة إذا كانت في نفس مكان تصوير آخر ({shootsPerDay({ team, hours: team.minHours })} جلسة
        كحد أقصى في اليوم).
      </p>

      <div className="flex flex-wrap gap-2 border-t border-white/5 pt-3">
        <Button variant="subtle" icon="pencil" onClick={onEdit}>
          تعديل
        </Button>
        <Button variant="subtle" icon="image" onClick={onPortfolio}>
          الأعمال ({(team.portfolio || []).length})
        </Button>
        <Button variant="ghost" icon="trash-2" onClick={onDelete} className="!text-red-300">
          حذف
        </Button>
      </div>
    </Panel>
  );
}

function Stat({ label, value, tone = 'text-white' }) {
  return (
    <div>
      <dt className="text-[10px] text-slate-500">{label}</dt>
      <dd className={`font-bold ${tone}`}>{value}</dd>
    </div>
  );
}

/* ========================================================================== */

function TeamEditor({ team: initial, busy, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});

  const update = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const toggleDay = (day) =>
    setForm((current) => {
      const days = current.workingDays || [];
      return {
        ...current,
        workingDays: days.includes(day) ? days.filter((d) => d !== day) : [...days, day],
      };
    });

  const submit = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'اسم الفريق مطلوب';
    if (!(Number(form.hourlyRate) >= 0)) next.hourlyRate = 'سعر غير صحيح';
    if (Number(form.minHours) > Number(form.maxHours)) next.minHours = 'أقل مدة أكبر من أقصاها';
    if (form.startTime >= form.endTime) next.endTime = 'وقت الانتهاء قبل البداية';
    if ((form.workingDays || []).length === 0) next.workingDays = 'اختر يوم عمل واحداً على الأقل';
    setErrors(next);
    if (Object.keys(next).length === 0) onSave(form);
  };

  const hours = Number(form.minHours) || 1;
  const occupied = occupiedHours({ hours, bufferMinutes: form.bufferMinutes });
  const occupiedSame = occupiedHours({
    hours,
    bufferMinutes: form.sameLocationBufferMinutes,
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? 'تعديل الفريق' : 'فريق تصوير جديد'}
      icon="camera"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            إلغاء
          </Button>
          <Button icon="save" loading={busy} onClick={submit}>
            حفظ
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="اسم الفريق" error={errors.name} required>
            <Input value={form.name} onChange={update('name')} placeholder="فريق التصوير الأول" />
          </Field>

          <Field label="سعر الساعة (ج.م)" error={errors.hourlyRate} required>
            <Input type="number" min="0" value={form.hourlyRate} onChange={update('hourlyRate')} />
          </Field>

          <Field label="البريد الإلكتروني" hint="يُستخدم لدخول الفريق عبر Google">
            <Input dir="ltr" value={form.email || ''} onChange={update('email')} />
          </Field>

          <Field label="رقم الهاتف">
            <Input dir="ltr" value={form.phone || ''} onChange={update('phone')} />
          </Field>

          <Field label="بداية الدوام">
            <TimeInput value={form.startTime} onChange={update('startTime')} />
          </Field>

          <Field label="نهاية الدوام" error={errors.endTime}>
            <TimeInput value={form.endTime} onChange={update('endTime')} />
          </Field>

          <Field label="أقل مدة (ساعات)" error={errors.minHours}>
            <Input type="number" min="1" value={form.minHours} onChange={update('minHours')} />
          </Field>

          <Field label="أقصى مدة (ساعات)">
            <Input type="number" min="1" value={form.maxHours} onChange={update('maxHours')} />
          </Field>
        </div>

        {/* The buffer, with its cost spelled out */}
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
          <Field
            label="الانتقال بين مكانين مختلفين (دقيقة)"
            hint="الوقت الذي يحتاجه الفريق للتنقل — لا يمكن حجزه لطبيب آخر"
          >
            <div className="flex flex-wrap gap-2">
              {BUFFER_PRESETS.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setForm((c) => ({ ...c, bufferMinutes: value }))}
                  aria-pressed={Number(form.bufferMinutes) === value}
                  className={`rounded-xl border px-3 py-2 text-xs font-bold transition-colors ${
                    Number(form.bufferMinutes) === value
                      ? 'border-amber-500 bg-amber-500/15 text-amber-200'
                      : 'border-white/10 bg-slate-900 text-slate-300 hover:border-white/25'
                  }`}
                >
                  {value === 0 ? 'بدون' : `${value} دقيقة`}
                </button>
              ))}
              <Input
                type="number"
                min="0"
                max="480"
                value={form.bufferMinutes}
                onChange={update('bufferMinutes')}
                className="w-24"
                aria-label="وقت انتقال مخصص"
              />
            </div>
          </Field>

          <div className="mt-4 border-t border-amber-500/20 pt-4">
            <Field
              label="الفاصل بين تصويرين في نفس المكان (دقيقة)"
              hint="الفريق موجود بالفعل — يحتاج وقت تجهيز فقط، لا انتقال"
            >
              <div className="flex flex-wrap gap-2">
                {SAME_LOCATION_PRESETS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setForm((c) => ({ ...c, sameLocationBufferMinutes: value }))}
                    aria-pressed={Number(form.sameLocationBufferMinutes) === value}
                    className={`rounded-xl border px-3 py-2 text-xs font-bold transition-colors ${
                      Number(form.sameLocationBufferMinutes) === value
                        ? 'border-emerald-500 bg-emerald-500/15 text-emerald-200'
                        : 'border-white/10 bg-slate-900 text-slate-300 hover:border-white/25'
                    }`}
                  >
                    {value === 0 ? 'بدون' : `${value} دقيقة`}
                  </button>
                ))}
                <Input
                  type="number"
                  min="0"
                  max="480"
                  value={form.sameLocationBufferMinutes}
                  onChange={update('sameLocationBufferMinutes')}
                  className="w-24"
                  aria-label="فاصل مخصص لنفس المكان"
                />
              </div>
            </Field>
          </div>

          <p className="mt-3 text-[11px] leading-relaxed text-amber-200">
            جلسة {hours} ساعة تشغل <strong>{occupied}</strong> ساعة في مكان جديد، أو{' '}
            <strong>{occupiedSame}</strong> ساعة في نفس مكان تصوير آخر — أي{' '}
            {shootsPerDay({ team: form, hours })} جلسة كحد أقصى في اليوم.
          </p>
          <p className="mt-1 text-[11px] text-slate-500">
            تغيير هذين الرقمين لا يؤثر على الحجوزات القائمة — كل حجز يحتفظ بالفواصل التي حُجز بها.
          </p>
        </div>

        <Field label="أيام العمل" error={errors.workingDays}>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((day) => (
              <button
                key={day}
                type="button"
                onClick={() => toggleDay(day)}
                aria-pressed={(form.workingDays || []).includes(day)}
                className={`rounded-xl border px-3 py-2 text-xs font-bold transition-colors ${
                  (form.workingDays || []).includes(day)
                    ? 'border-purple-500 bg-purple-500/15 text-purple-200'
                    : 'border-white/10 bg-slate-900 text-slate-400 hover:border-white/25'
                }`}
              >
                {ARABIC_DAYS[day]}
              </button>
            ))}
          </div>
        </Field>

        <Field label="نبذة عن الفريق" hint="تظهر للطبيب عند الاختيار">
          <Textarea rows={3} value={form.bio || ''} onChange={update('bio')} />
        </Field>

        <Checkbox
          label="الفريق متاح للحجز"
          checked={form.active !== false}
          onChange={(e) => setForm((c) => ({ ...c, active: e.target.checked }))}
        />
      </div>
    </Modal>
  );
}

/* ========================================================================== */

/** Sample work a doctor sees before choosing a crew. */
function PortfolioEditor({ team, onClose, onSaved }) {
  const { success, error: toastError } = useNotifications();
  const [items, setItems] = useState(team.portfolio || []);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);

  const addFiles = async (fileList) => {
    const incoming = Array.from(fileList || []);
    if (items.length + incoming.length > MAX_PORTFOLIO) {
      toastError(`الحد الأقصى ${MAX_PORTFOLIO} ملفاً`);
      return;
    }

    setReading(true);
    try {
      const read = [];
      for (const file of incoming) {
        // Images are compressed; video is stored as-is because re-encoding it
        // in the browser would destroy it.
        const data = file.type.startsWith('image/')
          ? await compressImage(file, { maxWidth: 1400, quality: 0.78 })
          : await readFileAsDataUri(file);

        if (dataUriByteLength(data) > MAX_FILE_BYTES) {
          toastError(`"${file.name}" أكبر من ${formatBytes(MAX_FILE_BYTES)}`);
          continue;
        }
        read.push({ data, name: file.name, type: file.type, caption: '' });
      }
      setItems((current) => [...current, ...read]);
    } catch (err) {
      toastError(`تعذر قراءة الملف: ${err.message}`);
    } finally {
      setReading(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await photographyApi.setPortfolio(team.id, items);
      success('تم حفظ أعمال الفريق');
      onSaved();
    } catch (err) {
      toastError(err.message || 'تعذر حفظ الأعمال');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`أعمال ${team.name}`}
      icon="image"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            إلغاء
          </Button>
          <Button icon="save" loading={busy} onClick={save}>
            حفظ
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <label
          className={`flex min-h-[80px] cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed p-4 text-sm transition-all ${
            reading
              ? 'border-purple-500 bg-purple-500/10 text-purple-300'
              : 'border-white/20 text-slate-400 hover:border-purple-500/50 hover:text-purple-400'
          }`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            addFiles(e.dataTransfer.files);
          }}
        >
          <Icon
            name={reading ? 'loader-2' : 'image-plus'}
            size={20}
            className={reading ? 'animate-spin' : ''}
          />
          {reading ? 'جاري قراءة الملفات...' : 'اسحب صوراً أو فيديوهات، أو اضغط للاختيار'}
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </label>

        {items.length === 0 ? (
          <EmptyState icon="image" title="لم تُضف أعمال بعد" />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {items.map((item, index) => (
              <div
                key={`${item.name}-${index}`}
                className="overflow-hidden rounded-xl border border-white/10 bg-slate-900"
              >
                {String(item.data || '').startsWith('data:video/') ? (
                  <video src={item.data} controls className="h-36 w-full bg-black object-contain" />
                ) : (
                  <img src={item.data} alt="" className="h-36 w-full object-cover" />
                )}

                <div className="p-2">
                  <Input
                    value={item.caption || ''}
                    onChange={(e) =>
                      setItems((current) =>
                        current.map((entry, i) =>
                          i === index ? { ...entry, caption: e.target.value } : entry,
                        ),
                      )
                    }
                    placeholder="وصف مختصر"
                    className="!py-1.5 text-[11px]"
                  />
                  <button
                    type="button"
                    onClick={() => setItems((current) => current.filter((_, i) => i !== index))}
                    className="mt-1.5 w-full rounded-lg bg-red-500/10 py-1 text-[11px] font-bold text-red-300 hover:bg-red-500/20"
                  >
                    إزالة
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-[11px] text-slate-500">
          الصور تُضغط تلقائياً. الفيديوهات تُحفظ كما هي، لذا استخدم مقاطع قصيرة.
        </p>
      </div>
    </Modal>
  );
}

export default AdminPhotographyTeams;
